import * as React from 'react';
import { IProductReviewItem } from 'PyzShopUi/scripts/product-review/interfaces';
import ProductRating from 'PyzShopUi/scripts/shop-ui/components/product-item/ProductRating';
import { useTranslationHook } from 'PyzShopUi/scripts/utils/translationHook';
import InfoBox from 'PyzShopUi/scripts/shop-ui/components/InfoBox';
import Tooltip from 'PyzShopUi/scripts/react/common/components/Tooltip';
import Loading from 'PyzShopUi/scripts/react/common/components/Loading';
import LanguageHandler from 'PyzShopUi/scripts/modules/language';
import classNames from 'classnames';
import useDeeplTranslation from 'PyzShopUi/scripts/shop-ui/hooks/useDeeplTranslation';
import { TRANSLATE_FAILED_ERROR_TIME } from 'PyzShopUi/scripts/product-review/constants';

interface IProductReviewItemProps {
    productReviewItem: IProductReviewItem;
}

const ProductReviewItem: React.FunctionComponent<IProductReviewItemProps> = props => {
    const { productReviewItem } = props;
    const { translate } = useTranslationHook();

    const currentLanguageRef = React.useRef<string>(LanguageHandler.getCurrentLanguage());
    const [hasError, setHasError] = React.useState<boolean>(false);

    const {
        deeplTranslate,
        cancelDeeplTranslate,
        translatedSummary,
        translatedDescription,
        translatedComment,
        isLoading,
        isTranslated,
        isError,
        errorMessage
    } = useDeeplTranslation(
        productReviewItem.summary,
        productReviewItem.description,
        productReviewItem.repliedComment || '',
        productReviewItem.language
    );

    React.useEffect(() => {
        if (!isError) {
            return;
        }

        setHasError(true);
        setTimeout(() => {
            setHasError(false);
        }, TRANSLATE_FAILED_ERROR_TIME);

    }, [isError])

    const displayPurchasedOn = () => {
        if (!productReviewItem.purchasedOn) {
            return null;
        }

        return (
            <div className="product-review-detail__item--rating--created_at">
                {translate('Purchase date', { ns: 'article-detail' })}:&nbsp;{productReviewItem.purchasedOn}
            </div>
        );
    };

    const displayVerified = () => {
        if (!productReviewItem.isVerified) {
            return null;
        }

        return (
            <div className="product-review-detail__item--rating--verified">
                <Tooltip
                    title={translate('Verified', { ns: 'article-detail' })}
                    id={`tooltip-verified-${productReviewItem.idProductReview}`}
                    content={translate('The “verified” note next to a review indicates that the review was submitted following a confirmed customer purchase.', { ns: 'article-detail' })}
                    isHoverEnabled={false}
                    showInfoIcon={false}
                    tooltipText={translate('Verified', { ns: 'article-detail' })}
                />
            </div>
        );
    };

    const displayReviewDetail = () => {
        if (isLoading) {
            return <Loading/>;
        }

        const splitDescription = (translatedDescription || '').toString().split(/\r?\n|\r|\n/g);

        return (
            <React.Fragment>
                <div className="product-review-detail__item--detail">
                    <h3>{translatedSummary}</h3>
                    {
                        splitDescription.map((content, index) => (
                            <p key={index}>{content}</p>
                        ))
                    }
                </div>
                {displayTranslateButton()}
                {displayRepliedComment()}
            </React.Fragment>
        );
    };

    const displayRepliedComment = () => {
        if (!translatedComment) {
            return null;
        }

        const splitRepliedComments = (translatedComment).toString().split(/\r?\n|\r|\n/g);

        return (
            <div className="product-review-detail__item--comment">
                <InfoBox>
                    {
                        splitRepliedComments.map((content, index) => (
                            <p key={index} className="paragraph-x">
                                {
                                    index === 0
                                        ? (<b>{translate('Louis Customer Service', { ns: 'article-detail' })}: &nbsp;</b>)
                                        : <></>
                                }
                                <span>{content}</span>
                            </p>
                        ))
                    }
                </InfoBox>
            </div>
        );
    };

    const displayTranslateButton = () => {
        if (!productReviewItem.language || currentLanguageRef.current === productReviewItem.language) {
            return;
        }

        if (isTranslated) {
            return (
                <div 
                    onClick={cancelDeeplTranslate}
                    onKeyDown={(e) => {
                        if (e.key === "Enter") {
                            cancelDeeplTranslate()
                        }
                    }}
                    role="button"
                    tabIndex={0}
                    className="product-review-detail__item--translate-button"
                >
                    <span>{translate('Display original', { ns: 'article-review' })}</span>
                </div>
            );
        }

        return (
            <div 
                onClick={deeplTranslate}
                onKeyDown={(e) => {
                    if (e.key === "Enter") {
                        deeplTranslate()
                    }
                }}
                role="button"
                tabIndex={0}
                className="product-review-detail__item--translate-button"
            >
                <span>{translate('Automatic translation', { ns: 'article-review' })}</span>
            </div>
        );
    };

    const displayErrorMessageBox = () => {
        const errorMessageClass = classNames(
            'error-message-box',
            {
                'fade-in': hasError,
            },
        );

        return (
            <div className={errorMessageClass}>
                <InfoBox boxType="error">
                    <p className="paragraph-x">{errorMessage}</p>
                </InfoBox>
            </div>
        );
    };

    return (
        <div className="product-review-detail__item">
            {displayErrorMessageBox()}
            <div className="product-review-detail__item--rating">
                <ProductRating ratingAverage={productReviewItem.rating}/>
                <div className="product-review-detail__item--rating--created_at">
                    <span className="created-at-label">
                        {translate('Created on', { ns: 'article-detail' })}:&nbsp;
                    </span>
                    {productReviewItem.createdAt}
                </div>

                {displayPurchasedOn()}

                <div className="product-review-detail__item--rating--reviewer">
                    {translate('Reviewed by', { ns: 'article-detail' })}:&nbsp;{productReviewItem.nickname}
                </div>

                {displayVerified()}
            </div>
            {displayReviewDetail()}
        </div>
    );
};

export default ProductReviewItem;
